<script lang="ts">
  // Two small forms of the board: « Nouvelle tâche » (same fields as /tache, plus another
  // requester) and « Passer la main » (a task dropped into « En attente »: to whom, with a
  // comment). Native <dialog>, like « Nouvelle conversation ».
  import { onMount } from 'svelte';
  import { api, ApiError, type Conversation, type Message } from '../lib/api';
  import { app } from '../lib/stores.svelte';
  import { handoffTarget } from '../lib/tasks';

  let {
    conversation,
    handoffFor = null,
    onClose,
  }: { conversation: Conversation; handoffFor?: Message | null; onClose: () => void } = $props();

  let dialog: HTMLDialogElement | undefined = $state();
  let error = $state('');
  let busy = $state(false);
  const me = app.me?.id ?? '';
  const members = $derived(conversation.members);

  // New task
  let title = $state('');
  let body = $state('');
  let assignee = $state('');
  let requester = $state(me);
  let due = $state('');

  // Hand-over
  // svelte-ignore state_referenced_locally
  let to = $state(handoffFor?.task ? (handoffTarget(handoffFor.task, me)?.id ?? '') : '');
  let comment = $state('');

  onMount(() => dialog?.showModal());

  async function submit(event: SubmitEvent) {
    event.preventDefault();
    error = '';
    busy = true;
    try {
      if (handoffFor) {
        if (!comment.trim()) {
          error = 'Ajoutez un commentaire ou un document pour passer la main';
          return;
        }
        app.upsertTask(await api.handoff(handoffFor.id, to, comment, [], crypto.randomUUID()));
      } else {
        const message = await api.createTask(conversation.id, {
          title,
          body,
          assignee_id: assignee || null,
          requester_id: requester,
          due_date: due || null,
          client_id: crypto.randomUUID(),
        });
        app.addMessage(message, false);
        app.upsertTask(message);
      }
      dialog?.close();
    } catch (e) {
      error = e instanceof ApiError ? e.message : 'Une erreur est survenue';
    } finally {
      busy = false;
    }
  }
</script>

<dialog bind:this={dialog} onclose={onClose} aria-labelledby="task-dialog-title">
  <header>
    <h2 id="task-dialog-title">{handoffFor ? 'Passer la main' : 'Nouvelle tâche'}</h2>
    <button type="button" class="close" aria-label="Fermer" onclick={() => dialog?.close()}>×</button>
  </header>
  <form onsubmit={submit}>
    {#if handoffFor}
      <p class="hint">« {handoffFor.task?.title} » attendra la réponse de la personne choisie.</p>
      <label class="field">À qui
        <select class="input" required bind:value={to}>
          <option value="" disabled>Choisir une personne</option>
          {#each members.filter((m) => m.id !== handoffFor?.task?.waiting_on?.id) as m (m.id)}
            <option value={m.id}>{m.display_name}{m.id === me ? ' (vous)' : ''}</option>
          {/each}
        </select>
      </label>
      <label class="field">Commentaire
        <textarea class="input" rows="4" required bind:value={comment} placeholder="Ce que vous attendez de cette personne"></textarea>
      </label>
      <p class="hint">Pour joindre un document, passez la main depuis le détail de la tâche.</p>
    {:else}
      <label class="field">Intitulé
        <input class="input" required maxlength="200" bind:value={title} placeholder="Corriger l'export des biens archivés" />
      </label>
      <div class="grid">
        <label class="field">Responsable
          <select class="input" bind:value={assignee}>
            <option value="">Non attribuée</option>
            {#each members as m (m.id)}<option value={m.id}>{m.display_name}{m.id === me ? ' (vous)' : ''}</option>{/each}
          </select>
        </label>
        <label class="field">Demandeur
          <select class="input" bind:value={requester}>
            {#each members as m (m.id)}<option value={m.id}>{m.display_name}{m.id === me ? ' (vous)' : ''}</option>{/each}
          </select>
        </label>
        <label class="field">Échéance
          <input class="input" type="date" bind:value={due} />
        </label>
      </div>
      <label class="field">Description (facultative)
        <textarea class="input" rows="4" bind:value={body}></textarea>
      </label>
    {/if}
    {#if error}<p class="error" role="alert">{error}</p>{/if}
    <div class="actions">
      <button type="button" class="btn" onclick={() => dialog?.close()}>Annuler</button>
      <button type="submit" class="btn primary" disabled={busy}>{handoffFor ? 'Passer la main' : 'Créer la tâche'}</button>
    </div>
  </form>
</dialog>

<style>
  dialog {
    width: min(520px, calc(100vw - 32px));
    max-height: calc(100vh - 64px);
    border: 1px solid var(--border);
    border-radius: var(--radius-l);
    padding: 20px;
    background: var(--bg);
    color: var(--ink);
  }
  dialog::backdrop {
    background: rgba(16, 24, 40, 0.4);
  }
  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 12px;
  }
  h2 {
    margin: 0;
    font-size: 18px;
  }
  .close {
    width: 32px;
    height: 32px;
    border: 0;
    border-radius: var(--radius-s);
    background: transparent;
    font-size: 22px;
    color: var(--muted);
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 12px;
  }
  textarea.input {
    height: auto;
    padding: 8px 10px;
    resize: vertical;
  }
  .hint {
    margin: 0;
    font-size: 13px;
    color: var(--muted);
  }
  .actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
</style>
